import { useStudioStore } from "@/store/studio-store";
import { CogoLogoBrand, CogoWordmark } from "@/components/cogo-logo";
import type { StudioRoute } from "@/store/types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Home,
  FolderOpen,
  Settings,
  ExternalLink,
  LogOut,
  LogIn,
  Boxes,
  UploadCloud,
  Rocket,
  Bot,
  MessagesSquare,
  Palette,
  BarChart3,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react'
import { useCallback, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react'
import { pushStudioRoute, replaceStudioRoute } from '@/lib/studio-navigation'
import {
  STUDIO_GLOBAL_NAV_SECTIONS,
  studioRouteAncestry,
  studioRouteLabel,
} from '@/lib/studio-route-registry'
import { useUIBreakpoint } from '@/hooks/use-ui-breakpoint'
import { CREATOR_NAV_OPEN_EVENT } from '@/shell/creator-nav-events'
import { StudioLanguageSwitcher, useStudioLocale } from '@/i18n'
import type { StudioDictionary } from '@/i18n/studio-dictionaries'
import { cogoXyzRegistry } from '@/features/cogo-xyz/cogo-xyz-registry'

const SECTION_ICONS: Partial<Record<StudioRoute, typeof Home>> = {
  home: Home,
  projects: FolderOpen,
  import: UploadCloud,
  'design-system': Palette,
  library: Boxes,
  agent: Bot,
  bots: MessagesSquare,
  'apps-live': Rocket,
  commerce: BarChart3,
}

const navSections = STUDIO_GLOBAL_NAV_SECTIONS.map((section) => ({
  ...section,
  items: section.routes.map((route) => ({
    route,
    icon: SECTION_ICONS[route]!,
  })),
}))

const subscribeToCogoXyzRegistry = (listener: () => void) =>
  cogoXyzRegistry.subscribe(listener)
const readCogoXyzRegistryRevision = () => cogoXyzRegistry.getRevision()

const visibleNavigationRoutes = new Set<StudioRoute>([
  ...navSections.flatMap((section) => section.items.map((item) => item.route)),
  'settings',
])

function owningNavigationRoute(route: StudioRoute): StudioRoute {
  return (
    studioRouteAncestry(route)
      .reverse()
      .find((ancestor) => visibleNavigationRoutes.has(ancestor)) ?? route
  )
}

function RailIconButton({
  label,
  onClick,
  isActive = false,
  variant = "default",
  children,
  testId,
  badge,
}: {
  label: string;
  onClick: () => void;
  isActive?: boolean;
  variant?: "default" | "destructive";
  children: ReactNode;
  testId?: string;
  badge?: string | null;
}) {
  const baseColor = isActive
    ? "bg-muted text-foreground font-semibold"
    : variant === "destructive"
      ? "text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
      : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground";
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-label={badge ? `${label}: ${badge}` : label}
          aria-current={isActive ? 'page' : undefined}
          data-testid={testId}
          className={`relative flex h-10 w-10 items-center justify-center rounded-xl transition-all ${baseColor}`}
        >
          {children}
          {badge ? (
            <span aria-hidden className="absolute right-0 top-0 inline-flex min-h-4 min-w-4 items-center justify-center rounded-full border border-background bg-foreground px-1 text-[9px] font-semibold leading-none text-background">
              {badge}
            </span>
          ) : null}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * The rail's own collapse control.
 *
 * It is named for the state it MOVES TO, not the state it is in, because the
 * accessible name is read at the moment of choosing: "Collapse navigation" on
 * an expanded rail tells the user what pressing it does. `aria-expanded`
 * separately reports the state it is in, which is the pair a screen reader
 * needs to describe both halves.
 */
function RailCollapseToggle({
  expanded,
  onToggle,
  labels,
}: {
  expanded: boolean;
  onToggle: () => void;
  labels: StudioDictionary["navigation"];
}) {
  const label = expanded ? labels.collapseNavigation : labels.expandNavigation;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onToggle}
          aria-label={label}
          aria-expanded={expanded}
          aria-controls="creator-nav-panel"
          data-testid="creator-nav-collapse-toggle"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {expanded ? (
            <PanelLeftClose className="h-4 w-4" strokeWidth={1.75} />
          ) : (
            <PanelLeftOpen className="h-4 w-4" strokeWidth={1.75} />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

function AccountMenu({
  displayName,
  email,
  initial,
  expanded,
  onSignOut,
  labels,
}: {
  displayName: string;
  email: string;
  initial: string;
  expanded: boolean;
  onSignOut: () => void;
  labels: StudioDictionary["navigation"];
}) {
  // `displayName` falls back to the email when the account has no
  // `full_name` — which is every email/password signup on the appliance — so
  // the chip printed the same address twice, once as the name and once as the
  // address under it. The second line exists to say WHICH account the name
  // belongs to; when the name already is the address there is nothing left to
  // disambiguate, so it is dropped rather than repeated.
  const showsEmailSeparately = email.length > 0 && email !== displayName;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="creator-nav-profile"
          aria-label={`${labels.account}: ${email}`}
          className={
            expanded
              ? "group flex w-full items-center gap-3 rounded-xl px-1.5 py-2 text-left transition-colors hover:bg-foreground/5"
              : "group flex justify-center py-1"
          }
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-foreground text-xs font-bold text-background shadow-sm transition-colors group-hover:bg-foreground/90">
            {initial}
          </span>
          {expanded && (
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="studio-ellipsis text-[13px] font-semibold text-foreground">
                {displayName}
              </span>
              {showsEmailSeparately && (
                <span className="studio-ellipsis mt-0.5 text-[11px] leading-none text-muted-foreground/80">
                  {email}
                </span>
              )}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="end"
        sideOffset={10}
        className="w-56 p-2"
      >
        <div className="border-b border-border/60 px-2 pb-2 pt-1">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {labels.account}
          </div>
          <div className="studio-ellipsis mt-1 text-xs font-semibold text-foreground">
            {displayName}
          </div>
          {showsEmailSeparately && (
            <div className="studio-ellipsis text-[11px] text-muted-foreground">
              {email}
            </div>
          )}
        </div>
        <a
          href="/"
          data-testid="creator-nav-coogo-home"
          className="mt-1 flex min-h-10 w-full items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-foreground hover:bg-muted"
        >
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
          {labels.coogoHomepage}
        </a>
        <button
          type="button"
          onClick={onSignOut}
          data-testid="creator-nav-signout"
          className="mt-1 flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
        >
          <LogOut className="h-4 w-4" strokeWidth={1.5} />
          {labels.signOut}
        </button>
      </PopoverContent>
    </Popover>
  );
}

function SignInButton({
  expanded,
  onSignIn,
  label,
}: {
  expanded: boolean;
  onSignIn: () => void;
  label: string;
}) {
  if (!expanded) {
    return (
      <RailIconButton
        label={label}
        onClick={onSignIn}
        testId="creator-nav-signin"
      >
        <LogIn className="h-5 w-5" strokeWidth={1.5} />
      </RailIconButton>
    );
  }

  return (
    <button
      type="button"
      onClick={onSignIn}
      data-testid="creator-nav-signin"
      className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
    >
      <LogIn className="h-5 w-5 shrink-0" strokeWidth={1.5} />
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
}

export function CreatorNavPanel() {
  const { strings } = useStudioLocale();
  const studioRoute = useStudioStore((s) => s.studioRoute);
  const setStudioRoute = useStudioStore((s) => s.setStudioRoute);
  const currentProjectId = useStudioStore((s) => s.currentProjectId);
  const currentProjectName = useStudioStore((s) => s.currentProjectName);
  const userProjects = useStudioStore((s) => s.userProjects);
  const isLoadingUserProjects = useStudioStore((s) => s.isLoadingUserProjects);
  const userProjectsError = useStudioStore((s) => s.userProjectsError);
  const userProjectsLoadedAt = useStudioStore((s) => s.userProjectsLoadedAt);
  const closeProject = useStudioStore((s) => s.closeProject);
  const user = useStudioStore((s) => s.user);
  const signOut = useStudioStore((s) => s.signOut);
  const navRailExpanded = useStudioStore((s) => s.navRailExpanded);
  const toggleNavRail = useStudioStore((s) => s.toggleNavRail);
  const { bp } = useUIBreakpoint();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isMobileShell =
    bp === "md" && typeof window !== "undefined" && window.innerWidth < 1024;
  // The mobile shell is a full-width drawer the user opened on purpose and
  // dismisses by choosing something; a 56px rail inside it would be a
  // 56px column floating in a 304px sheet. Collapsing is a desktop
  // affordance, so only the desktop rail reads the stored preference.
  const effectiveExpanded = isMobileShell ? true : navRailExpanded;
  const activeNavigationRoute = owningNavigationRoute(studioRoute);
  useSyncExternalStore(
    subscribeToCogoXyzRegistry,
    readCogoXyzRegistryRevision,
    readCogoXyzRegistryRevision,
  );
  const registrySnapshot = cogoXyzRegistry.snapshot();
  const pendingApprovalCount = registrySnapshot.toolRuns.filter((run) => {
    const outputStatus = run.output?.status;
    return String(run.status) === 'approval_required' || outputStatus === 'approval_required';
  }).length;
  const renderedNavSections = navSections
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          item.route !== 'agent' ||
          pendingApprovalCount > 0 ||
          activeNavigationRoute === 'agent',
      ),
    }))
    .filter((section) => section.items.length > 0);

  const badgeForRoute = (route: StudioRoute): string | null => {
    if (route === 'projects') {
      if (userProjectsError) return '!';
      if (isLoadingUserProjects || userProjectsLoadedAt === null) return null;
      return String(userProjects.length);
    }
    if (route === 'agent' && pendingApprovalCount > 0) return String(pendingApprovalCount);
    return null;
  };

  const displayName = user?.user_metadata?.full_name ?? user?.email ?? "";
  const email = user?.email ?? "";
  const initial =
    displayName.charAt(0).toUpperCase() || email.charAt(0).toUpperCase() || "U";

  const sidebarWidth = isMobileShell
    ? "w-full"
    : effectiveExpanded
      ? "w-[232px]"
      : "w-14";

  const closeMobileNav = useCallback(() => {
    if (isMobileShell) setMobileNavOpen(false);
  }, [isMobileShell]);

  useEffect(() => {
    if (!isMobileShell) {
      setMobileNavOpen(false);
      return;
    }

    const openMobileNav = () => setMobileNavOpen(true);
    window.addEventListener(CREATOR_NAV_OPEN_EVENT, openMobileNav);
    return () =>
      window.removeEventListener(CREATOR_NAV_OPEN_EVENT, openMobileNav);
  }, [isMobileShell]);

  const handleLogoClick = useCallback(() => {
    if (currentProjectId) closeProject();
    closeMobileNav();
    pushStudioRoute("home");
  }, [currentProjectId, closeProject, closeMobileNav]);

  const handleSignOut = useCallback(async () => {
    if (currentProjectId) closeProject();
    closeMobileNav();
    await signOut();
    replaceStudioRoute("home");
  }, [currentProjectId, closeProject, closeMobileNav, signOut]);

  const handleSignIn = useCallback(() => {
    if (currentProjectId) closeProject();
    closeMobileNav();
    window.history.pushState(null, "", "/login");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [currentProjectId, closeProject, closeMobileNav]);

  const navPanel = (
    <TooltipProvider delayDuration={300}>
      <aside
        id="creator-nav-panel"
        data-testid="creator-nav-panel"
        className={`flex h-full ${sidebarWidth} flex-col border-r border-border bg-background py-4 transition-all duration-200 shrink-0 select-none ${effectiveExpanded ? "px-4" : "px-0 items-center"}`}
      >
        {/* Logo and Brand */}
        <div
          className={`mb-5 flex w-full items-center ${effectiveExpanded ? "px-1" : "flex-col justify-center gap-1"}`}
        >
          <button
            type="button"
            onClick={handleLogoClick}
            aria-label={strings.navigation.goHome}
            data-testid="creator-nav-brand"
            className={`flex h-14 min-w-0 items-center rounded-xl bg-transparent px-1.5 transition-colors hover:bg-muted/80 ${effectiveExpanded ? "w-full" : "w-10 justify-center px-0"}`}
          >
            {/* The horizontal lockup is 104px wide at size 40 and cannot fit a
                56px rail. The square mark is the same brand at a width the
                collapsed rail has; the button's `aria-label` carries the name
                either way, so nothing is lost by swapping the artwork. */}
            {effectiveExpanded ? (
              <CogoWordmark size={40} />
            ) : (
              <CogoLogoBrand size={28} />
            )}
          </button>
          {!isMobileShell && (
            <RailCollapseToggle
              expanded={effectiveExpanded}
              onToggle={toggleNavRail}
              labels={strings.navigation}
            />
          )}
        </div>

        {/* Separator */}
        <div
          aria-hidden
          className={`h-px bg-border/60 mb-4 font-mono shrink-0 ${effectiveExpanded ? "w-full" : "w-6"}`}
        />

        {effectiveExpanded && currentProjectId ? (
          <div
            data-testid="creator-nav-current-workspace"
            className="mb-4 w-full rounded-lg border border-border bg-card px-3 py-2"
          >
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {strings.navigation.work}
            </div>
            <div className="studio-ellipsis mt-1 text-xs font-semibold text-foreground">
              {currentProjectName ?? currentProjectId}
            </div>
          </div>
        ) : null}

        {/* Nav Items */}
        <nav
          className={`flex flex-col gap-3 w-full flex-1 overflow-y-auto ${effectiveExpanded ? "" : "items-center"}`}
        >
          {renderedNavSections.map((section) => (
            <div
              key={section.id}
              className={`flex w-full flex-col gap-1.5 ${effectiveExpanded ? "" : "items-center"}`}
            >
              {effectiveExpanded ? (
                <div className="px-3 pt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/80">
                  {strings.navigation.sections[section.id]}
                </div>
              ) : (
                <div aria-hidden className="my-1 h-px w-6 bg-border/60" />
              )}

              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = activeNavigationRoute === item.route;
                const label = studioRouteLabel(item.route, strings);
                const badge = badgeForRoute(item.route);

                const handleClick = () => {
                  if (currentProjectId && item.route !== 'agent') closeProject();
                  closeMobileNav();
                  setStudioRoute(item.route);
                  pushStudioRoute(item.route);
                };

                if (!effectiveExpanded) {
                  return (
                    <RailIconButton
                      key={item.route}
                      label={label}
                      isActive={isActive}
                      onClick={handleClick}
                      testId={`creator-nav-${item.route}`}
                      badge={badge}
                    >
                      <Icon
                        className="h-5 w-5"
                        strokeWidth={isActive ? 2 : 1.5}
                      />
                    </RailIconButton>
                  );
                }

                return (
                  <button
                    key={item.route}
                    type="button"
                    onClick={handleClick}
                    data-testid={`creator-nav-${item.route}`}
                    aria-current={isActive ? 'page' : undefined}
                    className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all w-full text-left ${
                      isActive
                        ? "bg-muted text-foreground font-semibold"
                        : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
                    }`}
                  >
                    <Icon
                      className={`h-5 w-5 shrink-0 ${isActive ? "text-foreground" : "text-muted-foreground"}`}
                      strokeWidth={isActive ? 2 : 1.5}
                    />
                    <span
                      className={`text-sm font-medium ${isActive ? "text-foreground font-semibold" : "text-muted-foreground group-hover:text-foreground"}`}
                    >
                      {label}
                    </span>
                    {badge ? (
                      <span
                        className="ml-auto inline-flex min-h-5 min-w-5 items-center justify-center rounded-full border border-border bg-background px-1.5 text-[10px] font-semibold text-muted-foreground"
                        aria-label={`${label}: ${badge}`}
                      >
                        {badge}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Bottom Section (Settings + Theme Toggle + Avatar Profile) */}
        <div
          className={`flex flex-col gap-3 w-full border-t border-border/60 pt-4 mt-auto shrink-0 ${effectiveExpanded ? "" : "items-center"}`}
        >
          <StudioLanguageSwitcher
            compact={!effectiveExpanded}
            className={
              effectiveExpanded
                ? "w-full justify-start gap-3 rounded-xl px-3 hover:bg-foreground/5"
                : "w-10 justify-center rounded-xl p-0 hover:bg-foreground/5"
            }
          />

          {/* Settings button */}
          {(() => {
            const isActive = activeNavigationRoute === "settings";
            const handleClick = () => {
              if (currentProjectId) closeProject();
              closeMobileNav();
              setStudioRoute("settings");
              pushStudioRoute("settings");
            };

            if (!effectiveExpanded) {
              return (
                <RailIconButton
                  label={studioRouteLabel("settings", strings)}
                  isActive={isActive}
                  onClick={handleClick}
                  testId="creator-nav-settings"
                >
                  <Settings
                    className="h-5 w-5"
                    strokeWidth={isActive ? 2 : 1.5}
                  />
                </RailIconButton>
              );
            }

            return (
              <button
                type="button"
                onClick={handleClick}
                data-testid="creator-nav-settings"
                aria-current={isActive ? 'page' : undefined}
                className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all w-full text-left ${
                  isActive
                    ? "bg-muted text-foreground font-semibold"
                    : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
                }`}
              >
                <Settings
                  className={`h-5 w-5 shrink-0 ${isActive ? "text-foreground" : "text-muted-foreground"}`}
                  strokeWidth={isActive ? 2 : 1.5}
                />
                <span
                  className={`text-sm font-medium ${isActive ? "text-foreground" : "text-muted-foreground group-hover:text-foreground"}`}
                >
                  {studioRouteLabel("settings", strings)}
                </span>
              </button>
            );
          })()}

          {/* User profile avatar info block */}
          <div
            className={`h-px bg-border/60 my-1 font-mono shrink-0 ${
              effectiveExpanded ? "w-full" : "w-6"
            }`}
          />

          {user ? (
            <AccountMenu
              displayName={displayName}
              email={email}
              initial={initial}
              expanded={effectiveExpanded}
              onSignOut={() => void handleSignOut()}
              labels={strings.navigation}
            />
          ) : (
            <SignInButton
              expanded={effectiveExpanded}
              onSignIn={handleSignIn}
              label={strings.navigation.signIn}
            />
          )}
        </div>
      </aside>
    </TooltipProvider>
  );

  if (isMobileShell) {
    return (
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="left"
          data-testid="creator-nav-mobile-sheet"
          className="w-[304px] max-w-[82vw] p-0"
        >
          <SheetTitle className="sr-only">
            {strings.navigation.studioNavigation}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {strings.navigation.studioNavigationDescription}
          </SheetDescription>
          {navPanel}
        </SheetContent>
      </Sheet>
    );
  }

  return navPanel;
}
